import { memo } from "react";

import { IconCreditCard, IconFileInvoice, IconListDetails } from "@tabler/icons";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";

import { TableThWithSortIcon } from "@sso/components/TableThWithSortIcon";
import { Dialog, OrderStatus, UserRole } from "@sso/constant";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { selectCurrentUser } from "@sso/store/slices/auth";
import { openDialog } from "@sso/store/slices/dialog";
import { setOrderSortColumn } from "@sso/store/slices/order";
import { ID, OrderSummary, TableColumn, OrderSummaryRowKeys, PaymentType, Currency } from "@sso/types";
import { RowContainer } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";
import { LayoutTable, TD, TH, TableWrapper } from "@sso/ui/Table";
import { localStorageHelper } from "@sso/utils";

interface OrdersTableProps {
  data: OrderSummary[];
  orderTableColumns: TableColumn<string>[];
  dataKeys: OrderSummaryRowKeys[];
}

const getStatusLabel = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.OPEN:
      return "Pending payment";
    case OrderStatus.COMPLETE:
      return "Paid";
    case OrderStatus.EXPIRED:
      return "Expired";
    default:
      return "Unknown";
  }
};

const getPaymentLabel = (paymentMode: PaymentType) => {
  switch (paymentMode) {
    case PaymentType.OneOffPayment:
      return "One off Payment";
    case PaymentType.RecurringPayment:
      return "Recurring Payment";
    case PaymentType.PaymentOnAccount:
      return "Payment on Account";
    case PaymentType.Free:
      return "Free";
    default:
      return "Unknown";
  }
};

const formatPrice = (price: number, currency: Currency) => {
  return `${currency.symbol}${price.toLocaleString("en-US", {
    minimumFractionDigits: currency.isZeroDecimal ? 0 : 2,
    maximumFractionDigits: currency.isZeroDecimal ? 0 : 2,
  })}`;
};

export const OrdersTable = memo(({ data, orderTableColumns, dataKeys }: OrdersTableProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);
  const isAdmin = user?.role.roleId === UserRole.ADMINISTRATOR;
  const dispatch = useAppDispatch();
  const handleSortAction = (column: string, multiField?: string[]) => {
    dispatch(setOrderSortColumn({ column, multiField }));
  };

  const rows = data.map(order => ({
    ...order,
    [OrderSummaryRowKeys.CREATED_DATE]: new Date(order.createdDate).toLocaleDateString(),
    [OrderSummaryRowKeys.USER]: order.fullName,
    [OrderSummaryRowKeys.ORGANISATION]: order.organisationName,
    [OrderSummaryRowKeys.VALUE]: formatPrice(order.total, order.transactionCurrency),
    [OrderSummaryRowKeys.PAYMENT_MODE]: getPaymentLabel(order.paymentMode),
    [OrderSummaryRowKeys.STATUS_LABEL]: getStatusLabel(order.status),
  })) as any;

  const viewInvoice = (invoiceUrl: string) => {
    window.open(invoiceUrl, "_blank");
  };

  const openDetailsDialog = (id: ID) => {
    dispatch(openDialog({ type: Dialog.DETAILS_ORDER, id }));
  };

  const continueOrder = (orderSummary: any) => {
    navigate({
      pathname: "/payment",
      search: `?orderId=${orderSummary.id}&clientId=${process.env
        .REACT_APP_CLIENT_ID!}&token=${localStorageHelper.getItem("SSO_TOKEN")}`,
    });
  };

  return (
    <TableWrapper>
      <LayoutTable>
        <thead>
          <tr>
            {orderTableColumns.map(col => (
              <TH
                key={col.field}
                sortable={col.sortable}
                onClick={() => col.sortable && handleSortAction(col.field, col.multiField)}
              >
                {col.sortable ? <TableThWithSortIcon column={col.field} title={col.name} /> : col.name}
              </TH>
            ))}
            {isAdmin && <TH textAlign="center">Actions</TH>}
          </tr>
        </thead>
        <tbody>
          {rows.map((order: any) => (
            <tr key={order.id}>
              {dataKeys.map(key => (
                <TD key={key}>
                  <RowContainer width="100%" justify={key === OrderSummaryRowKeys.VALUE ? "flex-end" : "flex-start"}>
                    {order[key]}
                  </RowContainer>
                </TD>
              ))}

              <TD>
                <RowContainer justify="center">
                  <IconButton
                    color={theme.colors.grey[700]}
                    hoverColor={theme.colors.info.main}
                    onClick={() => openDetailsDialog(order.id)}
                    title="View Order Details"
                  >
                    <IconListDetails />
                  </IconButton>
                  {order.invoiceUrl && (
                    <IconButton
                      color={theme.colors.grey[700]}
                      hoverColor={theme.colors.info.main}
                      onClick={() => viewInvoice(order.invoiceUrl!)}
                      title="View Invoice"
                    >
                      {" "}
                      <IconFileInvoice />
                    </IconButton>
                  )}

                  {order.status === OrderStatus.OPEN && (
                    <IconButton
                      color={theme.colors.grey[700]}
                      hoverColor={theme.colors.info.main}
                      onClick={() => continueOrder(order)}
                      title="Continue Order"
                    >
                      {" "}
                      <IconCreditCard />
                    </IconButton>
                  )}
                </RowContainer>
              </TD>
            </tr>
          ))}
        </tbody>
      </LayoutTable>
    </TableWrapper>
  );
});
